/**
 * PropertyContactsTab
 *
 * Tab for managing property contacts on premises liability coverages.
 * Allows adding/editing property owners, managers, and other contacts.
 */
import {
  Building2,
  Mail,
  MapPin,
  Phone,
  Plus,
  Trash2,
  User,
} from 'lucide-react';
import * as React from 'react';

import {
  emptyMailingAddress,
  formatMailingAddress,
  isMailingAddressComplete,
  mailingAddressFromSources,
  mailingAddressHasAnyValue,
  normalizeMailingAddress,
} from '@/components/coverage/propertyContactAddress';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  MailingAddress,
  PropertyContact,
  PropertyContactType,
  PropertyRecipientCategory,
} from '@/services/types/coverage-types';

interface PropertyContactFormData {
  id: string;
  contact_type: PropertyContactType;
  custom_type_label?: string | null;
  name?: string | null;
  phone?: string;
  email?: string;
  mailing_address: MailingAddress;
  recipient_category?: PropertyRecipientCategory | null;
  notes?: string;
}

interface PropertyContactsTabProps {
  contacts: PropertyContactFormData[];
  onChange: (contacts: PropertyContactFormData[]) => void;
  savedContacts?: PropertyContact[] | null;
  isReadOnly?: boolean;
}

const emptyContact = (): PropertyContactFormData => ({
  id: crypto.randomUUID(),
  contact_type: PropertyContactType.PROPERTY_OWNER,
  name: '',
  phone: '',
  email: '',
  mailing_address: emptyMailingAddress(),
  recipient_category: null,
  notes: '',
});

const getRecipientCategoryLabel = (
  category: PropertyRecipientCategory,
): string => {
  switch (category) {
    case PropertyRecipientCategory.CORPORATE_ENTITY:
      return 'Corporate Entity';
    case PropertyRecipientCategory.REGISTERED_AGENT:
      return 'Registered Agent';
    case PropertyRecipientCategory.PROPERTY_MANAGEMENT:
      return 'Property Management';
    case PropertyRecipientCategory.PRIVATE_RESIDENT:
      return 'Private Resident';
    case PropertyRecipientCategory.GOVERNMENT:
      return 'Government';
    default:
      return category;
  }
};

const getContactTypeLabel = (type: PropertyContactType): string => {
  switch (type) {
    case PropertyContactType.PROPERTY_OWNER:
      return 'Property Owner';
    case PropertyContactType.PROPERTY_MANAGER:
      return 'Property Manager';
    case PropertyContactType.OTHER:
      return 'Other';
    default:
      return type;
  }
};

export const PropertyContactsTab: React.FC<PropertyContactsTabProps> = ({
  contacts,
  onChange,
  savedContacts,
  isReadOnly = false,
}) => {
  const handleAddContact = () => {
    onChange([...contacts, emptyContact()]);
  };

  const handleRemoveContact = (id: string) => {
    onChange(contacts.filter((c) => c.id !== id));
  };

  const handleContactChange = (
    id: string,
    field: keyof PropertyContactFormData,
    value: string | null,
  ) => {
    onChange(contacts.map((c) => (c.id === id ? { ...c, [field]: value } : c)));
  };

  const handleMailingAddressChange = (
    id: string,
    field: keyof MailingAddress,
    value: string,
  ) => {
    onChange(
      contacts.map((c) =>
        c.id === id
          ? {
              ...c,
              mailing_address: {
                ...c.mailing_address,
                [field]: value,
              },
            }
          : c,
      ),
    );
  };

  // Show saved contacts summary if in read-only mode
  if (isReadOnly && savedContacts?.length) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-zinc-900">
            Property Contacts ({savedContacts.length})
          </h3>
        </div>
        <div className="grid gap-3">
          {savedContacts.map((contact, index) => {
            const phoneHandle = contact.handles?.find(
              (h) => h.handle_type === 'PHONE',
            );
            const emailHandle = contact.handles?.find(
              (h) => h.handle_type === 'EMAIL',
            );
            return (
              <Card key={index} className="border-zinc-200">
                <CardContent className="py-3 px-4">
                  <div className="flex items-start justify-between">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-zinc-900">
                          {contact.name || 'Unnamed Contact'}
                        </span>
                        <Badge variant="secondary" className="text-xs">
                          {contact.contact_type === PropertyContactType.OTHER &&
                          contact.custom_type_label
                            ? contact.custom_type_label
                            : getContactTypeLabel(contact.contact_type)}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-zinc-600">
                        {phoneHandle?.value && (
                          <span className="flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {phoneHandle.value}
                          </span>
                        )}
                        {emailHandle?.value && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {emailHandle.value}
                          </span>
                        )}
                        {(contact.mailing_address || contact.address) && (
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {formatMailingAddress(contact.mailing_address) ||
                              contact.address}
                          </span>
                        )}
                      </div>
                      {contact.notes && (
                        <p className="text-xs text-zinc-500 mt-1">
                          {contact.notes}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-semibold text-zinc-900">
            Property Contacts
          </h3>
          <p className="text-xs text-zinc-500 mt-1">
            Add property owners, managers, or other contacts for this premises
            coverage. Each contact can receive a Letter of Representation (LOR).
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddContact}
          className="gap-1"
        >
          <Plus className="h-3.5 w-3.5" />
          Add Contact
        </Button>
      </div>

      {contacts.length === 0 ? (
        <div className="text-center py-8 border-2 border-dashed border-zinc-200 rounded-lg">
          <Building2 className="h-8 w-8 text-zinc-400 mx-auto mb-2" />
          <p className="text-sm text-zinc-600 mb-3">
            No property contacts added yet
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleAddContact}
            className="gap-1"
          >
            <Plus className="h-3.5 w-3.5" />
            Add Contact
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {contacts.map((contact, index) => (
            <Card key={contact.id} className="border-zinc-200">
              <CardHeader className="pb-3 pt-4 px-4">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <User className="h-4 w-4 text-violet-600" />
                    Contact {index + 1}
                  </CardTitle>
                  {contacts.length > 1 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveContact(contact.id)}
                      className="h-7 w-7 p-0 text-zinc-400 hover:text-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="px-4 pb-4 space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  {/* Contact Type */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-zinc-700">
                      Contact Type
                    </Label>
                    <Select
                      value={contact.contact_type}
                      onValueChange={(value) =>
                        handleContactChange(
                          contact.id,
                          'contact_type',
                          value as PropertyContactType,
                        )
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={PropertyContactType.PROPERTY_OWNER}>
                          Property Owner
                        </SelectItem>
                        <SelectItem
                          value={PropertyContactType.PROPERTY_MANAGER}
                        >
                          Property Manager
                        </SelectItem>
                        <SelectItem value={PropertyContactType.OTHER}>
                          Other
                        </SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Recipient Category */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-zinc-700">
                      Recipient Category
                    </Label>
                    <Select
                      value={contact.recipient_category || '_none'}
                      onValueChange={(value) =>
                        handleContactChange(
                          contact.id,
                          'recipient_category',
                          value === '_none' ? null : value,
                        )
                      }
                    >
                      <SelectTrigger className="h-9">
                        <SelectValue placeholder="Select category..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none">Not set</SelectItem>
                        {Object.values(PropertyRecipientCategory).map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {getRecipientCategoryLabel(cat)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Custom Type Label (for OTHER) */}
                  {contact.contact_type === PropertyContactType.OTHER && (
                    <div className="space-y-2">
                      <Label className="text-xs font-medium text-zinc-700">
                        Contact Role
                      </Label>
                      <Input
                        placeholder="e.g., Insurance Agent"
                        value={contact.custom_type_label || ''}
                        onChange={(e) =>
                          handleContactChange(
                            contact.id,
                            'custom_type_label',
                            e.target.value,
                          )
                        }
                        className="h-9"
                      />
                    </div>
                  )}

                  {/* Name */}
                  <div
                    className={`space-y-2 ${contact.contact_type !== PropertyContactType.OTHER ? 'col-span-1' : ''}`}
                  >
                    <Label className="text-xs font-medium text-zinc-700">
                      Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      placeholder="Contact name"
                      value={contact.name || ''}
                      onChange={(e) =>
                        handleContactChange(contact.id, 'name', e.target.value)
                      }
                      className="h-9"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  {/* Phone */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-zinc-700 flex items-center gap-1">
                      <Phone className="h-3 w-3" />
                      Phone
                    </Label>
                    <Input
                      placeholder="(555) 555-5555"
                      value={contact.phone || ''}
                      onChange={(e) =>
                        handleContactChange(contact.id, 'phone', e.target.value)
                      }
                      className="h-9"
                    />
                  </div>

                  {/* Email */}
                  <div className="space-y-2">
                    <Label className="text-xs font-medium text-zinc-700 flex items-center gap-1">
                      <Mail className="h-3 w-3" />
                      Email
                    </Label>
                    <Input
                      type="email"
                      placeholder="contact@example.com"
                      value={contact.email || ''}
                      onChange={(e) =>
                        handleContactChange(contact.id, 'email', e.target.value)
                      }
                      className="h-9"
                    />
                  </div>
                </div>

                {/* Mailing Address */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-zinc-700 flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    Mailing Address
                  </Label>
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      placeholder="Street address"
                      value={contact.mailing_address.address_line1 || ''}
                      onChange={(e) =>
                        handleMailingAddressChange(
                          contact.id,
                          'address_line1',
                          e.target.value,
                        )
                      }
                      className="h-9"
                    />
                    <Input
                      placeholder="Apt, suite, unit, etc. (optional)"
                      value={contact.mailing_address.address_line2 || ''}
                      onChange={(e) =>
                        handleMailingAddressChange(
                          contact.id,
                          'address_line2',
                          e.target.value,
                        )
                      }
                      className="h-9"
                    />
                    <Input
                      placeholder="City"
                      value={contact.mailing_address.city || ''}
                      onChange={(e) =>
                        handleMailingAddressChange(contact.id, 'city', e.target.value)
                      }
                      className="h-9"
                    />
                    <div className="grid grid-cols-2 gap-4">
                      <Input
                        placeholder="State"
                        value={contact.mailing_address.state || ''}
                        onChange={(e) =>
                          handleMailingAddressChange(contact.id, 'state', e.target.value)
                        }
                        className="h-9"
                        maxLength={2}
                      />
                      <Input
                        placeholder="ZIP"
                        value={contact.mailing_address.zip_code || ''}
                        onChange={(e) =>
                          handleMailingAddressChange(
                            contact.id,
                            'zip_code',
                            e.target.value,
                          )
                        }
                        className="h-9"
                      />
                    </div>
                  </div>
                  {mailingAddressHasAnyValue(contact.mailing_address) &&
                    !isMailingAddressComplete(contact.mailing_address) && (
                      <p className="text-xs text-rose-600">
                        Street, city, state, and ZIP are required for certified
                        mail.
                      </p>
                    )}
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <Label className="text-xs font-medium text-zinc-700">
                    Notes
                  </Label>
                  <p className="text-xs text-zinc-500">
                    Include any notes for this contact here, including where
                    the ownership or contact information was found.
                  </p>
                  <Textarea
                    placeholder="Add notes about this contact"
                    value={contact.notes || ''}
                    onChange={(e) =>
                      handleContactChange(contact.id, 'notes', e.target.value)
                    }
                    rows={2}
                    className="resize-none"
                  />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Helper to convert form data to API format
export const formatPropertyContactsForApi = (
  contacts: PropertyContactFormData[],
): PropertyContact[] => {
  return contacts
    .filter((c) => c.name?.trim())
    .map((c) => ({
      id: c.id,
      contact_type: c.contact_type,
      custom_type_label:
        c.contact_type === PropertyContactType.OTHER
          ? c.custom_type_label
          : undefined,
      name: c.name?.trim() || '',
      handles: [
        c.phone?.trim()
          ? { handle_type: 'PHONE' as const, value: c.phone.trim() }
          : null,
        c.email?.trim()
          ? { handle_type: 'EMAIL' as const, value: c.email.trim() }
          : null,
      ].filter(Boolean) as PropertyContact['handles'],
      mailing_address: normalizeMailingAddress(c.mailing_address) || undefined,
      recipient_category: c.recipient_category || undefined,
      notes: c.notes?.trim() || undefined,
    }));
};

// Helper to convert API data to form data
export const mapPropertyContactsToFormData = (
  contacts: PropertyContact[] | null | undefined,
): PropertyContactFormData[] => {
  if (!contacts?.length) return [emptyContact()];

  return contacts.map((c) => {
    const phoneHandle = c.handles?.find((h) => h.handle_type === 'PHONE');
    const emailHandle = c.handles?.find((h) => h.handle_type === 'EMAIL');
    return {
      id: c.id || crypto.randomUUID(),
      contact_type: c.contact_type,
      custom_type_label: c.custom_type_label ?? null,
      name: c.name ?? null,
      phone: phoneHandle?.value || '',
      email: emailHandle?.value || '',
      mailing_address: mailingAddressFromSources(c.mailing_address),
      recipient_category: c.recipient_category ?? null,
      notes: c.notes || '',
    };
  });
};

export type { PropertyContactFormData };
